import { useEffect, useMemo, useState } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  ImageIcon,
  X,
} from 'lucide-react'
import { PdfViewer } from '@/components/files/PdfViewer'
import { Button } from '@/components/ui/Button'
import {
  downloadDataUrl,
  isImageName,
  isPdfName,
} from '@/lib/files'
import type { CatalogSopFile } from '@/services/catalog'

type SopViewerProps = {
  files: CatalogSopFile[]
  activeId: string
  onClose: () => void
  onSelect: (id: string) => void
  /** Catalog: true. Product: false (view only, no download / new tab). */
  allowDownload?: boolean
}

export function SopViewer({
  files,
  activeId,
  onClose,
  onSelect,
  allowDownload = true,
}: SopViewerProps) {
  const [broken, setBroken] = useState(false)
  const active = files.find((f) => f.id === activeId) ?? files[0]
  const images = useMemo(
    () => files.filter((f) => isImageName(f.name)),
    [files],
  )
  const imageIndex = useMemo(() => {
    if (!active || !isImageName(active.name)) return -1
    return images.findIndex((f) => f.id === active.id)
  }, [active, images])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    setBroken(false)
  }, [active?.id])

  if (!active) return null

  function go(delta: number) {
    if (imageIndex < 0 || images.length === 0) return
    const next = images[(imageIndex + delta + images.length) % images.length]
    onSelect(next.id)
  }

  async function handleDownload() {
    if (!allowDownload || !active?.dataUrl) return
    await downloadDataUrl(active.name, active.dataUrl)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/50 p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Xem SOP: ${active.name}`}
    >
      <div className="mx-auto flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        {files.length > 1 ? (
          <div className="no-print flex shrink-0 gap-1 overflow-x-auto border-b border-slate-200 bg-slate-50 px-2 py-1.5">
            {files.map((file) => {
              const selected = file.id === active.id
              return (
                <button
                  key={file.id}
                  type="button"
                  onClick={() => onSelect(file.id)}
                  className={`max-w-[180px] shrink-0 truncate rounded px-2 py-1 text-left text-xs font-medium ${
                    selected
                      ? 'bg-[var(--color-accent)] text-white'
                      : 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100'
                  }`}
                  title={file.name}
                >
                  {file.name}
                </button>
              )
            })}
          </div>
        ) : null}

        <div className="min-h-0 flex-1">
          {isPdfName(active.name) && active.dataUrl ? (
            <PdfViewer
              fileName={active.name}
              dataUrl={active.dataUrl}
              onClose={onClose}
              allowDownload={allowDownload}
              onDownload={allowDownload ? () => void handleDownload() : undefined}
            />
          ) : (
            <div className="flex h-full min-h-0 flex-col bg-slate-100">
              <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-900">
                    {active.name}
                  </p>
                  <p className="text-xs text-slate-400">
                    {allowDownload ? 'Xem trực tiếp' : 'Chỉ xem — không tải xuống'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {allowDownload ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!active.dataUrl}
                      onClick={() => void handleDownload()}
                    >
                      <Download className="h-3.5 w-3.5" />
                      Tải xuống
                    </Button>
                  ) : null}
                  <Button size="sm" variant="ghost" onClick={onClose} aria-label="Đóng">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="relative min-h-0 flex-1 overflow-auto bg-slate-200/70">
                {isImageName(active.name) && active.dataUrl && !broken ? (
                  <div className="relative flex min-h-full items-center justify-center p-6">
                    <img
                      src={active.dataUrl}
                      alt={active.name}
                      className="max-h-[min(80vh,900px)] max-w-full object-contain shadow"
                      onError={() => setBroken(true)}
                      draggable={false}
                      onContextMenu={
                        allowDownload ? undefined : (e) => e.preventDefault()
                      }
                    />
                    {images.length > 1 ? (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          className="absolute top-1/2 left-3 -translate-y-1/2"
                          onClick={() => go(-1)}
                          aria-label="Ảnh trước"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          className="absolute top-1/2 right-3 -translate-y-1/2"
                          onClick={() => go(1)}
                          aria-label="Ảnh sau"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex min-h-full flex-col items-center justify-center gap-2 px-6 text-center text-slate-500">
                    {isImageName(active.name) ? (
                      <ImageIcon className="h-8 w-8 text-slate-400" />
                    ) : (
                      <FileText className="h-8 w-8 text-slate-400" />
                    )}
                    <p className="text-sm font-medium text-slate-800">{active.name}</p>
                    <p className="text-xs">
                      {isPdfName(active.name) || isImageName(active.name)
                        ? 'Không có nội dung để xem. Hãy tải file lên lại.'
                        : 'Định dạng này chưa hỗ trợ xem trực tiếp.'}
                      {allowDownload ? ' Bạn vẫn có thể tải file về.' : ''}
                    </p>
                    {allowDownload && active.dataUrl ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => void handleDownload()}
                      >
                        <Download className="h-3.5 w-3.5" />
                        Tải xuống
                      </Button>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
